import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { DialogService } from '../dialog/dialog.service';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { FeaturesDataDispatcher } from '../features-data/features-data.dispatcher';
import { NavigationService } from '../navigation/navigation.service';
import { EstimateDataStore } from './estimate-data.store';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import { Estimate } from './estimate.model';
import { DocumentDataEffects } from '../document-data/document-data.effects';
import { EstimateDataService } from './estimate-data.service';
import { IState } from './estimate-data.reducer';
import { EstimateDataBuilder } from './estimate-data.builder';
import { AppStore } from '../../store';
import { UtilsService } from '../shared/services/utils/utils.service';
import { RendererService } from '../renderer/renderer.service';
import { concat, forkJoin, Observable, of } from 'rxjs';
import {
  DeleteEntityStore,
  deleteEntityStore,
  fetchFilters,
  FetchFilters,
  ResetSelectedList,
  resetSelectedList,
  SaveEntityStore,
  saveEntityStore,
  setDispatchedResponse,
  SetDispatchedResponse,
  setEditingEntity,
  SetSelectedList,
  setSelectedList,
} from '../entity-data/entity-data.actions';
import {
  catchError,
  concatMap,
  filter,
  flatMap,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  ActionsEnum,
  BatchMarkEstimateAsDoneNetwork,
  batchMarkEstimateAsDoneNetwork,
  BatchMarkEstimateAsDoneNetworkError,
  batchMarkEstimateAsDoneNetworkError,
  BatchMarkEstimateAsDoneNetworkSuccess,
  batchMarkEstimateAsDoneNetworkSuccess,
  BatchMarkEstimateAsDoneStore,
  batchMarkEstimateAsDoneStore,
  MarkEstimateAsDone,
  markEstimateAsDoneError,
  MarkEstimateAsDoneError,
  markEstimateAsDoneSuccess,
  MarkEstimateAsDoneSuccess,
  MarkSelectedEstimatesAsDone,
} from './estimate-data.actions';
import {
  EstimateFiltersEnum,
  EstimateStatusesEnum,
} from './estimate-data.data';
import { List, Set } from 'immutable';
import {
  BatchSaveEntityStore,
  batchSaveEntityStore,
} from '../entity-data/entity-data.actions';
import { showToast, ShowToast } from '../toaster/toaster.actions';
import { IToasterConfig, IToasterData } from '../toaster/toaster.data';
import { MessageToastComponent } from '../shared/components/message-toast/message-toast.component';
import { AccountDataStore } from '../account-data/account-data.store';

@Injectable()
export class EstimateDataEffects extends DocumentDataEffects<IState, Estimate> {
  constructor(
    private _estimateDataService: EstimateDataService,
    private _estimateDataBuilder: EstimateDataBuilder,
    estimateDataStore: EstimateDataStore,
    accountDataStore: AccountDataStore,
    utilsService: UtilsService,
    rendererService: RendererService,
    actions: Actions,
    appStore: AppStore,
    settingsDataStore: SettingsDataStore,
    featuresDataStore: FeaturesDataStore,
    translateService: TranslateService,
    globalBannerService: GlobalBannerService,
    dialogService: DialogService,
    navigationService: NavigationService,
    localStorageService: LocalStorageService,
    featuresDataDispatcher: FeaturesDataDispatcher
  ) {
    super(
      _estimateDataService,
      estimateDataStore,
      _estimateDataBuilder,
      accountDataStore,
      utilsService,
      rendererService,
      actions,
      appStore,
      settingsDataStore,
      featuresDataStore,
      translateService,
      globalBannerService,
      dialogService,
      navigationService,
      localStorageService,
      featuresDataDispatcher
    );
  }

  @Effect() loadSearchParams = this.loadSearchParams;
  @Effect() persistSearchParams = this.persistSearchParams;
  @Effect() fetchList = this.fetchList;
  @Effect() fetchFilters = this.fetchFilters;
  @Effect() triggerRefreshFiltersWithDelay = this
    .triggerRefreshFiltersWithDelay;
  @Effect() searchList = this.searchList;
  @Effect() fetchNextPage = this.fetchNextPage;
  @Effect() deleteSelectedEntities = this.deleteSelectedEntities;
  @Effect() batchDeleteEntityStore = this.batchDeleteEntityStore;
  @Effect() batchDeleteEntityNetwork = this.batchDeleteEntityNetwork;
  @Effect() batchSaveEntityStore = this.batchSaveEntityStore;
  @Effect() clearSelectedEntitiesOnFetchOrSearchList = this
    .clearSelectedEntitiesOnFetchOrSearchList;
  @Effect() addToLimitedSelectedList = this.addToLimitedSelectedList;
  @Effect() hideBulkLimitBanner = this.hideBulkLimitBanner;
  @Effect() printDocument = this.printDocument;
  @Effect() markAsPrinted = this.markAsPrinted;
  @Effect() getEntityById = this.getEntityById;
  @Effect() copyDocument = this.copyDocument;
  @Effect() saveCopiedDocument = this.saveCopiedDocument;
  @Effect() redirectToCopiedDocument = this.redirectToCopiedDocument;
  @Effect() sendDocument = this.sendDocument;
  @Effect() saveNewEntity = this.saveNewEntity;
  @Effect() saveNewEntityError = this.saveNewEntityError;
  @Effect() updateEntity = this.updateEntity;
  @Effect() createNewEditingEntity = this.createNewEditingEntity;
  @Effect() refreshEditingEntity = this.refreshEditingEntity;
  @Effect() setEditingEntityContent = this.setEditingEntityContent;
  @Effect() setEditingEntityProperties = this.setEditingEntityProperties;
  @Effect() setEditingEntityField = this.setEditingEntityField;
  @Effect() saveEditingEntity = this.saveEditingEntity;
  @Effect() saveExistingEntityError = this.saveExistingEntityError;
  @Effect() saveEntityStore = this.saveEntityStore;
  @Effect() deleteEntityStore = this.deleteEntityStore;
  @Effect() saveEntityToNetwork = this.saveEntityToNetwork;
  @Effect() deleteEntityNetwork = this.deleteEntityNetwork;
  @Effect() addDocumentItemToEditing = this.addDocumentItemToEditing;
  @Effect() updateDocumentItemInEditing = this.updateDocumentItemInEditing;
  @Effect() deleteDocumentItemFromEditing = this.deleteDocumentItemFromEditing;
  @Effect() moveDocumentItemInEditing = this.moveDocumentItemInEditing;
  @Effect() batchAddDocumentItemsToEditing = this
    .batchAddDocumentItemsToEditing;
  @Effect() updateTaxInEditing = this.updateTaxInEditing;
  @Effect() refreshListAndFiltersOnTaxYearChange = this
    .refreshListAndFiltersOnTaxYearChange;
  @Effect({ dispatch: false }) goToDocumentAddClientScreen = this
    .goToDocumentAddClientScreen;

  @Effect()
  markEstimateAsDone: Observable<
    | DeleteEntityStore
    | SaveEntityStore
    | MarkEstimateAsDoneSuccess
    | MarkEstimateAsDoneError
    | SetDispatchedResponse
  > = this.actions.pipe(
    ofType(ActionsEnum.MARK_ESTIMATE_AS_DONE),
    filter(
      ({ payload }: MarkEstimateAsDone) =>
        payload.entityType === this.entityType
    ),
    switchMap(({ payload }: MarkEstimateAsDone) => {
      const {
        dispatchId,
        estimate,
        status,
        setEditingDocument,
        listFilter,
      } = payload;

      return this._estimateDataService
        .markEstimateAsDone(estimate.id, status)
        .pipe(
          flatMap((estimateProps) => {
            const effects = [];
            const newEstimate = this.documentDataBuilder.buildEntity(
              estimateProps,
              null,
              false,
              estimate.isNew()
            );

            effects.push(
              deleteEntityStore(this.entityType, estimate, false),
              saveEntityStore(this.entityType, newEstimate, false, listFilter)
            );

            if (setEditingDocument) {
              effects.push(setEditingEntity(this.entityType, newEstimate));
            }

            effects.push(
              markEstimateAsDoneSuccess(
                dispatchId,
                this.entityType,
                newEstimate
              ),
              setDispatchedResponse(
                this.entityType,
                dispatchId,
                ActionsEnum.MARK_ESTIMATE_AS_DONE,
                true,
                newEstimate
              )
            );

            return concat(effects);
          }),
          catchError((error) =>
            concat([
              markEstimateAsDoneError(dispatchId, this.entityType, estimate),
              setDispatchedResponse(
                this.entityType,
                dispatchId,
                ActionsEnum.MARK_ESTIMATE_AS_DONE,
                false,
                estimate,
                error
              ),
            ])
          )
        );
    })
  );

  /**
   * Trigger fetch list filters after mark as done
   */
  @Effect()
  triggerRefreshFiltersOnMarkAsDone: Observable<FetchFilters> = this.actions.pipe(
    ofType(
      ActionsEnum.MARK_ESTIMATE_AS_DONE_SUCCESS,
      ActionsEnum.BATCH_MARK_ESTIMATE_AS_DONE_NETWORK_SUCCESS
    ),
    filter(
      ({ payload }: MarkEstimateAsDoneSuccess) =>
        payload.entityType === this.entityType
    ),
    switchMap(() => {
      return concat([
        fetchFilters(
          this.entityType,
          this.refreshFiltersDelay,
          EstimateFiltersEnum.pending
        ),
        fetchFilters(
          this.entityType,
          this.refreshFiltersDelay,
          EstimateFiltersEnum.done
        ),
      ]);
    })
  );

  /**
   * Trigger fetch list filters after mark as done
   */
  @Effect()
  markSelectedEstimatesAsDone: Observable<
    | BatchMarkEstimateAsDoneStore
    | ResetSelectedList
    | BatchMarkEstimateAsDoneNetwork
  > = this.actions.pipe(
    ofType(ActionsEnum.MARK_SELECTED_ESTIMATES_AS_DONE),
    filter(
      ({ payload }: MarkSelectedEstimatesAsDone) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: MarkSelectedEstimatesAsDone) => {
      const { status, listFilter } = payload;

      return this.documentDataStore.getSelectedEntities().pipe(
        take(1),
        filter((x) => !!x),
        flatMap((selectedDocuments: Set<Estimate>) => {
          const filteredList = selectedDocuments
            .filter(
              (estimate: Estimate) => estimate.states.estimateStatus !== status
            )
            .toList();

          return concat([
            batchMarkEstimateAsDoneStore(
              this.entityType,
              filteredList,
              status,
              listFilter
            ),
            resetSelectedList(this.entityType),
            batchMarkEstimateAsDoneNetwork(
              this.entityType,
              filteredList,
              status,
              listFilter
            ),
          ]);
        })
      );
    })
  );

  @Effect()
  batchMarkEstimatesAsDoneStore: Observable<BatchSaveEntityStore> = this.actions.pipe(
    ofType(ActionsEnum.BATCH_MARK_ESTIMATE_AS_DONE_STORE),
    filter(
      ({ payload }: BatchMarkEstimateAsDoneStore) =>
        payload.entityType === this.entityType
    ),
    map(({ payload }: BatchMarkEstimateAsDoneStore) => {
      const { estimates, status, listFilter } = payload;
      const estimatesProps: any = estimates
        .map((estimate: Estimate) => estimate.toJS())
        .toArray();
      const updatedEstimates = List(
        this.utilsService
          .markEstimatesAsDone(estimatesProps, status)
          .map((estimate) =>
            this._estimateDataBuilder.buildEntity(estimate, null, false, false)
          )
      );

      return batchSaveEntityStore(
        this.entityType,
        updatedEstimates,
        listFilter
      );
    })
  );

  @Effect()
  batchMarkEstimateAsDoneNetwork: Observable<
    | BatchSaveEntityStore
    | SetSelectedList
    | BatchMarkEstimateAsDoneNetworkSuccess
    | BatchMarkEstimateAsDoneNetworkError
  > = this.actions.pipe(
    ofType(ActionsEnum.BATCH_MARK_ESTIMATE_AS_DONE_NETWORK),
    filter(
      ({ payload }: BatchMarkEstimateAsDoneNetwork) =>
        payload.entityType === this.entityType
    ),
    concatMap(({ payload }: BatchMarkEstimateAsDoneNetwork) => {
      const { estimates, status, listFilter } = payload;
      const reqs = estimates
        .map((estimate: Estimate) =>
          this._estimateDataService
            .markEstimateAsDone(estimate.id, status)
            .pipe(
              map((estimateProps) =>
                this._estimateDataBuilder.buildEntity(
                  estimateProps,
                  null,
                  false,
                  estimate.isNew()
                )
              ),
              catchError(() => of(estimate)) // keep estimates that request failed
            )
        )
        .toArray();

      return forkJoin(reqs).pipe(
        flatMap((results: Estimate[]) => {
          const effects = [];
          const grouped = List(results).groupBy(
            (estimate: Estimate) => estimate.states.estimateStatus === status
          );
          const successEstimates = List<Estimate>(grouped.get(true));
          const failedEstimates = List<Estimate>(grouped.get(false));

          // if there're failed estimates, return them to list as selected
          if (failedEstimates.size) {
            effects.push(
              batchSaveEntityStore(
                this.entityType,
                failedEstimates as List<any>,
                listFilter
              ),
              setSelectedList(this.entityType, failedEstimates.toSet())
            );
          }

          // if there's at least one success estimate, show toast
          if (successEstimates.size) {
            effects.push(
              batchMarkEstimateAsDoneNetworkSuccess(
                this.entityType,
                successEstimates,
                status
              )
            );
          } else {
            // otherwise display error message
            effects.push(
              batchMarkEstimateAsDoneNetworkError(
                this.entityType,
                failedEstimates,
                status
              )
            );
          }

          return concat(effects);
        }),
        catchError(() =>
          of(
            batchMarkEstimateAsDoneNetworkError(
              this.entityType,
              estimates,
              status
            )
          )
        )
      );
    })
  );

  @Effect()
  batchMarkEstimateAsDoneNetworkSuccess: Observable<ShowToast> = this.actions.pipe(
    ofType(ActionsEnum.BATCH_MARK_ESTIMATE_AS_DONE_NETWORK_SUCCESS),
    filter(
      ({ payload }: BatchMarkEstimateAsDoneNetworkSuccess) =>
        payload.entityType === this.entityType
    ),
    map(({ payload }: BatchMarkEstimateAsDoneNetworkSuccess) => {
      const { estimates, status } = payload;
      const key =
        status === EstimateStatusesEnum.approved ? 'APPROVED' : 'DECLINED';
      const total = estimates.size;
      const label =
        total === 1
          ? this.translateService.instant(`DOCUMENT.ESTIMATE.TOAST.${key}`, {
              documentNumber: estimates.first().docNumber,
            })
          : this.translateService.instant(
              `DOCUMENT.ESTIMATE.TOAST.${key}_MULTIPLE`,
              {
                number: total,
              }
            );
      const toastData: IToasterData = { label, containsHtml: true };
      const toastConfig: IToasterConfig = {
        type: 'bulk-approve-decline-estimates',
        component: MessageToastComponent,
        data: toastData,
      };

      return showToast(toastConfig);
    })
  );

  @Effect({ dispatch: false })
  batchMarkEstimateAsDoneNetworkError = this.actions.pipe(
    ofType(ActionsEnum.BATCH_MARK_ESTIMATE_AS_DONE_NETWORK_ERROR),
    filter(
      ({ payload }: BatchMarkEstimateAsDoneNetworkError) =>
        payload.entityType === this.entityType
    ),
    tap(() =>
      this.globalBannerService.showSystemErrorBanner(
        new Error('all estimates failed to be marked as done')
      )
    )
  );
}
